import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CampaignService } from '../../../core/services/api/campaign.service';  
import { ErrorService } from '../../../core/services/error.service';
import { finalize } from 'rxjs/operators';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
    selector: 'app-one-pager-preview',
    templateUrl: './one-pager-preview.component.html',
    styleUrls: ['./one-pager-preview.component.scss'],
    standalone: false
})
export class OnePagerPreviewComponent implements OnInit {
  campaignId: number;
  slug: string;
  summary: any;
  isLoading = false;

  private safeUrlCache = new Map<string, SafeResourceUrl>();
  private imageUrlCache = new Map<string, string>();

  constructor(
    private route: ActivatedRoute,
    private campaignService: CampaignService,
    private errorService: ErrorService,
    private location: Location,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const idOrSlug = params.get('idOrSlug');
      this.slug = undefined;
      this.campaignId = undefined;

      if (!idOrSlug) return;

      if (isNaN(Number(idOrSlug))) {
        this.slug = idOrSlug;
      } else {
        this.campaignId = Number(idOrSlug);
      }

      this.loadSummary();
    });
  }

  loadSummary() {
    this.safeUrlCache.clear();
    this.imageUrlCache.clear();

    this.isLoading = true;
    const observable = this.slug
      ? this.campaignService.getCampaignSummaryBySlug(this.slug)
      : this.campaignService.getCampaignSummaryById(this.campaignId);

    observable.pipe(finalize(() => (this.isLoading = false))).subscribe({       
      next: (data) => {
        this.summary = data;
        this.slug = data?.slug || this.slug;
      },
      error: (err) => this.errorService.showError(err),
    });
  }

  get fields(): any[] {
    return this.summary?.draft?.fields || this.summary?.live?.fields || [];     
  }

  back() {
    this.location.back();
  }

  private cleanUrl(url: string): string {
    if (!url) return '';
    return url.trim().replace(/^`+|`+$/g, '');
  }

  getSafeUrl(url: string): SafeResourceUrl {
    const cleanedUrl = this.cleanUrl(url);
    if (!cleanedUrl) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    }

    if (this.safeUrlCache.has(cleanedUrl)) {
      return this.safeUrlCache.get(cleanedUrl)!;
    }

    try {
      const parsed = new URL(cleanedUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        const result = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
        this.safeUrlCache.set(cleanedUrl, result);
        return result;
      }
      const result = this.sanitizer.bypassSecurityTrustResourceUrl(parsed.toString());
      this.safeUrlCache.set(cleanedUrl, result);
      return result;
    } catch {
      const result = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
      this.safeUrlCache.set(cleanedUrl, result);
      return result;
    }
  }

  getImageUrl(url: string): string {
    const cleanedUrl = this.cleanUrl(url);
    if (!cleanedUrl) return '';

    if (this.imageUrlCache.has(cleanedUrl)) {
      return this.imageUrlCache.get(cleanedUrl)!;
    }

    try {
      const withScheme = /^https?:\/\//i.test(cleanedUrl) ? cleanedUrl : `https://${cleanedUrl}`;
      const parsed = new URL(withScheme);
      const result = ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : 'about:blank';
      this.imageUrlCache.set(cleanedUrl, result);
      return result;
    } catch {
      const result = 'about:blank';
      this.imageUrlCache.set(cleanedUrl, result);
      return result;
    }
  }

  // Convert a YouTube/Vimeo URL to its embeddable form; null if not a recognized video URL.
  private toEmbedUrl(raw: string): string | null {
    const url = this.cleanUrl(raw);
    if (!url) return null;
    const yt = url.match(/^.*(youtu\.be\/|shorts\/|live\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
    if (yt && yt[2].length === 11) return 'https://www.youtube.com/embed/' + yt[2] + '?rel=0&modestbranding=1';
    const vm = url.match(/(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/);
    if (vm && vm[4]) return 'https://player.vimeo.com/video/' + vm[4];
    return null;
  }

  // Sanitized embed URL for the <iframe>, or null → show the fallback link.
  getVideoEmbedUrl(raw: string): SafeResourceUrl | null {
    const embed = this.toEmbedUrl(raw);
    return embed ? this.getSafeUrl(embed) : null;
  }

  // Cleaned, https-prefixed original URL for the "Watch on YouTube" link.
  getWatchUrl(raw: string): string {
    return this.getImageUrl(raw);
  }

  getIconName(iconName: string): string {
    return iconName?.startsWith('fa-') ? iconName : 'fa-' + iconName;
  }

  trackByField(index: number, field: any): number {
    return field.sequenceNo ?? field.SequenceNo ?? index;
  }
}
